import { action, autorun, computed, makeObservable, observable } from 'mobx';

import { SOCKET_USER_QUEST_MILESTONE_COMPLETED } from '../../config/constants';
import { analytics } from '../../services/analytics';
import type { WidgetEventOptionalArgs } from '../../services/analytics/types';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import type { PlayerQuest, QuestSocketNotification, RewardPackage, UserQuestMilestone } from './types';

const isClaimableMilestone = ({ questMilestone, startedAt, completedAt }: UserQuestMilestone) =>
  questMilestone.type === 'claim_reward' && !!startedAt && !completedAt;

export class QuestStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    this.setQuestsFlag(resolveFlag('quests_feat_client', true));

    const subscription = this.subscribeToSocket.bind(this);

    ldClient?.on('ready', () => {
      this.setQuestsFlag(ldClient?.variation('quests_feat_client', true));
    });

    ldClient?.on('change', (changes) => {
      if ('quests_feat_client' in changes) {
        this.setQuestsFlag(changes.quests_feat_client.current);
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;

      if (isUserAuthorized && this.questsFlag) {
        socket.on(SOCKET_USER_QUEST_MILESTONE_COMPLETED, subscription);
      } else {
        socket.off(SOCKET_USER_QUEST_MILESTONE_COMPLETED, subscription);
      }
    });
  }

  public dialogTypes: FeatureManagerDialogType[] = ['EventTriggerQuests'];

  private isFetching: boolean = false;

  @observable
  public questsFlag: boolean = false;

  @observable
  public loading: boolean = false;

  @observable
  public isInfoImageDisplayed: boolean = false;

  @observable
  public currentPlayerQuest: PlayerQuest | null = null;

  @observable
  public isClaimReminderShown: boolean = false;

  // Claimed in this session; the socket update that sets completedAt can lag behind the claim or never arrive.
  @observable
  private claimedMilestoneIds = new Set<string>();

  @computed
  public get isQuestDialogOpen() {
    return this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenQuestDialog;
  }

  @action
  public setFetching(fetching: boolean) {
    this.isFetching = fetching;
  }

  @action
  public setLoading(loading: boolean) {
    this.loading = loading;
  }

  @action
  private setQuestsFlag(questsFlag: boolean) {
    this.questsFlag = questsFlag;
  }

  @action
  public setCurrentPlayerQuest(quest: PlayerQuest | null) {
    this.currentPlayerQuest = quest;
  }

  @action
  public clearCurrentPlayerQuest() {
    this.currentPlayerQuest = null;
    if (this.isQuestDialogOpen) {
      this.closeQuestDialog();
    }
  }

  @action
  public reset() {
    this.currentPlayerQuest = null;
    this.loading = false;
    this.isInfoImageDisplayed = false;
    this.isFetching = false;
    this.isClaimReminderShown = false;
    this.claimedMilestoneIds.clear();
    this.isFirstTimeOpen = { GC: false, SC: false };
  }

  @action
  public setInfoImageDisplayed(isDisplayed: boolean) {
    this.isInfoImageDisplayed = isDisplayed;
  }

  @action
  public setClaimReminderShown(isShown: boolean) {
    this.isClaimReminderShown = isShown;
  }

  public getMilestoneRewardPackage(order: number): RewardPackage | undefined {
    const milestone = this.getQuestMilestoneByOrder(order);
    return milestone?.questMilestone?.milestoneRewardPackage?.[0];
  }

  public isMilestoneCompleted(order: number): boolean {
    const milestone = this.getQuestMilestoneByOrder(order);
    return !!milestone?.completedAt;
  }

  private isUnclaimedMilestone(milestone: UserQuestMilestone): boolean {
    return isClaimableMilestone(milestone) && !this.claimedMilestoneIds.has(milestone.id);
  }

  public isMilestoneClaimable(order: number): boolean {
    const milestone = this.getQuestMilestoneByOrder(order);
    return !!milestone && this.isUnclaimedMilestone(milestone);
  }

  public isMilestoneClaimableById(milestoneId: string): boolean {
    const milestone = this.getQuestMilestoneById(milestoneId);
    if (!milestone || !this.currentPlayerQuest) {
      return false;
    }

    const milestoneOrder = milestone.questMilestone.order;
    return this.isMilestoneClaimable(milestoneOrder);
  }

  @computed
  public get claimableMilestone(): UserQuestMilestone | undefined {
    return this.currentPlayerQuest?.userQuest?.userQuestMilestones?.find((milestone) =>
      this.isUnclaimedMilestone(milestone),
    );
  }

  get hasClaimableMilestone(): boolean {
    return !!this.claimableMilestone;
  }

  get isQuestRewardClaimed(): boolean {
    return !!this.currentPlayerQuest?.completedAt;
  }

  get isQuestMilestonesCompleted(): boolean {
    return !!this.currentPlayerQuest?.completedAllMilestonesAt;
  }

  get isQuestOrdered() {
    return this.currentPlayerQuest?.ordered;
  }

  private isFirstTimeOpen: Record<CoinType, boolean> = {
    GC: false,
    SC: false,
  };

  private setFirstTimeOpen(coinType: CoinType) {
    this.isFirstTimeOpen[coinType] = true;
  }

  private resetFirstTimeOpen(coinType: CoinType) {
    this.isFirstTimeOpen[coinType] = false;
  }

  public getQuestMilestoneByOrder(order: number) {
    return this.currentPlayerQuest?.userQuest?.userQuestMilestones?.find(
      (milestone) => milestone.questMilestone.order === order,
    );
  }

  public getQuestMilestoneById(milestoneId: string): UserQuestMilestone | undefined {
    return this.currentPlayerQuest?.userQuest?.userQuestMilestones?.find((milestone) => milestone.id === milestoneId);
  }

  public getUserQuestMilestoneIdByOrder(order: number): string | null {
    const milestone = this.getQuestMilestoneByOrder(order);
    return milestone?.id || null;
  }

  public closeQuestDialog() {
    this.rootStore.featureManagerStore.onDialogFinished('EventTriggerQuests');
    this.rootStore.uiStore.setCloseDialog();
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    if (!this.questsFlag) return 'OFF';

    try {
      this.setFetching(true);

      const playerActiveQuest = await this.requestPlayerActiveQuest();

      if (playerActiveQuest) {
        this.setCurrentPlayerQuest(playerActiveQuest);
      } else {
        this.clearCurrentPlayerQuest();
      }

      return 'READY';
    } catch (error) {
      devError('Error fetching quest', error);
      return 'ERROR';
    } finally {
      this.setFetching(false);
    }
  });

  private async requestPlayerActiveQuest(): Promise<PlayerQuest | undefined> {
    const res = await getRequest<{ data: { playerActiveQuest?: PlayerQuest } }>('quest/player');
    return res.data.playerActiveQuest;
  }

  // Progress only arrives with milestone completions, so refresh it when the widget reopens the chase.
  // Never clears or switches the open chase; that stays with fetch().
  public refreshMilestoneProgress = singleFlight(async (): Promise<void> => {
    if (!this.questsFlag || !this.currentPlayerQuest) return;

    try {
      const playerActiveQuest = await this.requestPlayerActiveQuest();

      if (playerActiveQuest && playerActiveQuest.questKey === this.currentPlayerQuest?.questKey) {
        this.setCurrentPlayerQuest(playerActiveQuest);
      }
    } catch (error) {
      devError('Error refreshing quest progress', error);
    }
  });

  public startDialogs(_dialogType?: FeatureManagerDialogType) {
    if (this.currentPlayerQuest) {
      const { coinType } = this.rootStore.coinTypeStore;
      const isFirstTimeOpen = this.isFirstTimeOpen[coinType];

      if (!isFirstTimeOpen) {
        this.setFirstTimeOpen(coinType);
        this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenQuestDialog });
        return true;
      }
    }

    return false;
  }

  public async claimQuestReward() {
    this.setLoading(true);
    try {
      await postRequest(`quest/player/finish`);
    } catch (error) {
      devError('Error finishing quest', error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  public async claimCurrentRewardMilestone(userQuestMilestoneId: string) {
    if (!this.currentPlayerQuest) {
      throw new Error('No active quest found');
    }

    if (!this.isMilestoneClaimableById(userQuestMilestoneId)) {
      throw new Error(`User quest milestone with id ${userQuestMilestoneId} is not claimable`);
    }

    this.setLoading(true);

    try {
      await postRequest(`quest/player/milestone/claim`, { userQuestMilestoneId });
      this.markMilestoneClaimed(userQuestMilestoneId);
    } catch (error) {
      devError(`Error claiming milestone reward`, error);
      this.closeQuestDialog();
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // Leaves completedAt to the socket update: the milestone image keys its reveal animation on it.
  @action
  private markMilestoneClaimed(userQuestMilestoneId: string) {
    this.claimedMilestoneIds.add(userQuestMilestoneId);
    // Re-arm the reminder so another unclaimed mid reward still gets one.
    this.isClaimReminderShown = false;
  }

  public sendAnalyticsEvent({
    event_action = 'click',
    params,
  }: {
    event_action: string;
    params?: WidgetEventOptionalArgs;
  }) {
    const { EventTriggerQuests } = this.rootStore.featureManagerStore.dialogManagerData || {};
    const feature_order = EventTriggerQuests?.order || 0;

    const event = {
      quest_key: this.currentPlayerQuest?.questKey,
      event_action,
      triggered_by: 'click',
      feature_type: 'quest',
      feature_sub_type: 'quest',
      widget_sub_type: 'quest',
      feature_order,
      ...params,
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    void analytics.sendEvent('widget', event);
  }

  private subscribeToSocket(data: QuestSocketNotification) {
    if (data?.data.playerQuestData) {
      this.setCurrentPlayerQuest(data.data.playerQuestData);
      this.resetFirstTimeOpen(this.rootStore.coinTypeStore.coinType); // restting to pop the dialog on completed milestone
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['EventTriggerQuests']);
    }
  }
}
